/** Default grouped-row order when no price column sort is active. */
export { DEFAULT_PRICE_HISTORY_AGGREGATE_SORT as DEFAULT_AGGREGATE_TABLE_SORT } from "../../services/beefAiPricing";

const MONTH_SORT_ASC = "year(record_at),month(record_at)";
const MONTH_SORT_DESC = "-year(record_at),-month(record_at)";

const COLUMN_KEY_TO_DIRECTUS_SORT_FIELD = {
  month: MONTH_SORT_ASC,
  province: "province",
  retailer: "retailer",
  avgUnit: "avg.unit_price",
  lowUnit: "min.unit_price",
  highUnit: "max.unit_price",
};

const DIRECTUS_SORT_FIELD_TO_COLUMN_KEY = Object.fromEntries(
  Object.entries(COLUMN_KEY_TO_DIRECTUS_SORT_FIELD).map(([columnKey, field]) => [field, columnKey])
);

export const AGGREGATE_TABLE_SORTABLE_COLUMN_KEYS = new Set(
  Object.keys(COLUMN_KEY_TO_DIRECTUS_SORT_FIELD)
);

/** Signed header ids + labels for the card-view sort dropdown (same fields as the table). */
export const AGGREGATE_CARD_SORT_OPTIONS = [
  { value: "-month", label: "Month (newest)" },
  { value: "month", label: "Month (oldest)" },
  { value: "province", label: "Province (A–Z)" },
  { value: "-province", label: "Province (Z–A)" },
  { value: "retailer", label: "Retailer (A–Z)" },
  { value: "-retailer", label: "Retailer (Z–A)" },
  { value: "-avgUnit", label: "Avg (per kg) (high to low)" },
  { value: "avgUnit", label: "Avg (per kg) (low to high)" },
  { value: "-lowUnit", label: "Low (high to low)" },
  { value: "lowUnit", label: "Low (low to high)" },
  { value: "-highUnit", label: "High (high to low)" },
  { value: "highUnit", label: "High (low to high)" },
];

const DEFAULT_CARD_SORT_VALUE = "-month";

/**
 * BasicTable-style signed header id (`-avgUnit`, `month`) from current sort UI state.
 *
 * @param {{ columnKey?: string, direction?: "asc" | "desc" }} uiState
 * @returns {string}
 */
export function aggregateTableSignedHeaderIdFromUiState(uiState) {
  const columnKey = uiState?.columnKey;
  if (!columnKey || !AGGREGATE_TABLE_SORTABLE_COLUMN_KEYS.has(columnKey)) {
    return DEFAULT_CARD_SORT_VALUE;
  }
  return uiState.direction === "desc" ? `-${columnKey}` : columnKey;
}

/**
 * @param {string} columnKey
 * @param {"asc" | "desc"} direction
 * @returns {string | null}
 */
export function aggregateTableSortParamFromColumnKey(columnKey, direction) {
  if (columnKey === "month") {
    return direction === "desc" ? MONTH_SORT_DESC : MONTH_SORT_ASC;
  }

  const field = COLUMN_KEY_TO_DIRECTUS_SORT_FIELD[columnKey];
  if (!field) return null;
  return direction === "desc" ? `-${field}` : field;
}

/**
 * BasicTable passes signed header ids (e.g. `-avgUnit`, `avgUnit`).
 *
 * @param {string} signedHeaderId
 * @returns {string | null}
 */
export function aggregateTableSortParamFromSignedHeaderId(signedHeaderId) {
  if (!signedHeaderId) return null;
  const descending = signedHeaderId.startsWith("-");
  const columnKey = descending ? signedHeaderId.slice(1) : signedHeaderId;
  return aggregateTableSortParamFromColumnKey(columnKey, descending ? "desc" : "asc");
}

/**
 * Map a single-field Directus aggregate sort to table header state for sort labels.
 *
 * @param {string | null | undefined} sortParam
 * @returns {{ columnKey: string, direction: "asc" | "desc" }}
 */
function monthSortUiStateFromSortParam(sortParam) {
  if (sortParam === MONTH_SORT_ASC || sortParam.startsWith(`${MONTH_SORT_ASC},`)) {
    return { columnKey: "month", direction: "asc" };
  }
  if (sortParam === MONTH_SORT_DESC || sortParam.startsWith(`${MONTH_SORT_DESC},`)) {
    return { columnKey: "month", direction: "desc" };
  }
  return null;
}

export function aggregateTableSortUiStateFromSortParam(sortParam) {
  if (!sortParam) {
    return { columnKey: "", direction: "asc" };
  }

  const monthSortUiState = monthSortUiStateFromSortParam(sortParam);
  if (monthSortUiState) {
    return monthSortUiState;
  }

  if (sortParam.includes(",")) {
    return { columnKey: "", direction: "asc" };
  }

  const descending = sortParam.startsWith("-");
  const field = descending ? sortParam.slice(1) : sortParam;
  const columnKey = DIRECTUS_SORT_FIELD_TO_COLUMN_KEY[field] || "";

  return {
    columnKey,
    direction: descending ? "desc" : "asc",
  };
}
